import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { LuPlus, LuUndo } from 'react-icons/lu';
import { MdOutlineEdit } from 'react-icons/md';
import CustomTooltip from '../../../common/Tooltip';
import CustomAddModal from '../../../components/Common/CustomAddModal';
import { getCurrentUserId, getOrganizationId, getUserDetails } from '../../../utils';
import { getSingleAddressDetails } from '../AddressBook/actions';
import { editInvoice, getOrganizationDetails } from './action';
import { DownloadInvoice } from './downloadPdf';

const CreateInvoiceModal = ({
  invoiceId,
  setIsLoading,
  isEdit,
  invoiceDetail,
  selectedItems,
  counterPartyId,
  refetch,
  handleModalClose,
  onClose,
  opsId,
  setInvoiceDetail,
  contractType,
}) => {
  const currentUser = getUserDetails();
  const initialInvoiceDetails = invoiceDetail || selectedItems || [];
  const [allBankDetails, setAllBankDetails] = useState({});
  const [state, setState] = useState({
    userDetails: {
      personal: {},
      organization: {},
      address: {},
    },
    counterParty: {
      details: {},
      address: {},
    },
    deductions: [],
    newDeduction: { label: "", value: "" },
    bankDetails: {},
    invoiceDetail: initialInvoiceDetails,
    totals: {
      subtotal: 0,
      finalTotal: 0,
    },
    editingLabel: null,
    editingDescription: null,
    undoHistory: [],
    lastAction: null,
  });

  const handleDescriptionUpdate = (index, newDescription, type) => {
    const updatedInvoiceDetail = state.invoiceDetail?.map((item, i) => {
      if (i === index) {
        return {
          ...item,
          invoiceDetail: { ...item?.invoiceDetail, [type]: newDescription },
        };
      }
      return item;
    });

    setState((prev) => ({
      ...prev,
      invoiceDetail: updatedInvoiceDetail,
    }));

    setInvoiceDetail?.((prev) => ({
      ...prev,
      selectedItems: updatedInvoiceDetail,
    }));
  };

  const toggleEditDescription = (index, type) => {
    setState((prev) => ({
      ...prev,
      [type]: prev[type] === index ? null : index,
    }));
  };

  const handleCargoVesselRemove = (index) => {
    const updatedInvoiceDetail = state.invoiceDetail?.map((item, i) => {
      if (i === index) {
        const isCargo = item?.invoiceDetail?.isCargo;
        const removedValue = isCargo ? item?.cargoName : item?.vesselName;

        return {
          ...item,
          cargoName: isCargo ? "" : item?.cargoName,
          vesselName: isCargo ? item?.vesselName : "",
          previousCargoVessel: removedValue,
          showUndo: true,
        };
      }
      return item;
    });

    setState((prev) => ({
      ...prev,
      invoiceDetail: updatedInvoiceDetail,
    }));

    setInvoiceDetail?.((prev) => ({
      ...prev,
      selectedItems: updatedInvoiceDetail,
    }));
  };

  const [isBankModalOpen, setIsBankModalOpen] = useState(false);
  const [selectedBankType, setSelectedBankType] = useState("individual");
  const [localBankDetails, setLocalBankDetails] = useState({});

  const handleEditBankDetails = () => {
    setSelectedBankType("individual");
    setLocalBankDetails(allBankDetails["individualBankDetails"] || {});
    setIsBankModalOpen(true);
  };

  const handleBankTypeChange = (type) => {
    setSelectedBankType(type);
    setLocalBankDetails(allBankDetails[`${type}BankDetails`] || {});
  };

  const handleFieldChange = (field, value) => {
    setLocalBankDetails((prev) => ({ ...prev, [field]: value }));
  };

  const handleExtraChange = (index, field, value) => {
    const newExtra = [...(localBankDetails.extra || [])];
    newExtra[index][field] = value;
    setLocalBankDetails((prev) => ({ ...prev, extra: newExtra }));
  };

  const handleAddExtra = () => {
    setLocalBankDetails((prev) => ({
      ...prev,
      extra: [...(prev.extra || []), { label: "", value: "" }],
    }));
  };

  const handleRemoveExtra = (index) => {
    const newExtra = [...(localBankDetails.extra || [])];
    newExtra.splice(index, 1);
    setLocalBankDetails((prev) => ({ ...prev, extra: newExtra }));
  };

  const handleSaveBankDetails = () => {
    setState((prev) => ({
      ...prev,
      bankDetails: localBankDetails,
    }));
    setIsBankModalOpen(false);
  };

  const handlePortRemove = (index) => {
    const updatedInvoiceDetail = state.invoiceDetail?.map((item, i) => {
      if (i === index) {
        const removedValue = item?.port;
        return {
          ...item,
          port: "",
          previousPort: removedValue,
          showUndoPort: true,
        };
      }
      return item;
    });

    setState((prev) => ({
      ...prev,
      invoiceDetail: updatedInvoiceDetail,
    }));

    setInvoiceDetail?.((prev) => ({
      ...prev,
      selectedItems: updatedInvoiceDetail,
    }));
  };

  const handleUndo = (index) => {
    const itemToUndo = state.invoiceDetail[index];
    if (!itemToUndo.previousCargoVessel) return;

    const updatedInvoiceDetail = state.invoiceDetail?.map((item, i) => {
      if (i === index) {
        const isCargo = item?.invoiceDetail?.isCargo;
        return {
          ...item,
          cargoName: isCargo ? item?.previousCargoVessel : item?.cargoName,
          vesselName: isCargo ? item?.vesselName : item?.previousCargoVessel,
          showUndo: false,
          previousCargoVessel: null,
        };
      }
      return item;
    });

    setState((prev) => ({
      ...prev,
      invoiceDetail: updatedInvoiceDetail,
    }));

    setInvoiceDetail?.((prev) => ({
      ...prev,
      selectedItems: updatedInvoiceDetail,
    }));
  };

  const handlePortUndo = (index) => {
    const itemToUndo = state.invoiceDetail[index];
    if (!itemToUndo.previousPort) return;

    const updatedInvoiceDetail = state.invoiceDetail?.map((item, i) => {
      if (i === index) {
        return {
          ...item,
          port: item?.previousPort,
          showUndoPort: false,
          previousPort: null,
        };
      }
      return item;
    });

    setState((prev) => ({
      ...prev,
      invoiceDetail: updatedInvoiceDetail,
    }));

    setInvoiceDetail?.((prev) => ({
      ...prev,
      selectedItems: updatedInvoiceDetail,
    }));
  };

  useEffect(() => {
    const details = state.invoiceDetail || initialInvoiceDetails || [];
    let subtotal = 0;
    if (Array.isArray(details)) {
      subtotal = details.reduce((sum, item) => {
        return sum + (parseFloat(item?.invoiceDetail?.amt) || parseFloat(item?.amount) || 0);
      }, 0);
    }

    const totalDeductions = (state.deductions || []).reduce((sum, deduction) => {
      return sum + (parseFloat(deduction.value) || 0);
    }, 0);

    const finalTotal = Math.max(0, subtotal - totalDeductions);

    setState((prev) => ({
      ...prev,
      totals: {
        subtotal: Number(subtotal.toFixed(2)),
        finalTotal: Number(finalTotal.toFixed(2)),
      },
    }));
  }, [state.invoiceDetail, state.deductions, initialInvoiceDetails]);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const orgId = getOrganizationId();
        const userId = getCurrentUserId();
        const [orgRes, userRes] = await Promise.all([
          getOrganizationDetails(orgId),
          getSingleAddressDetails(userId),
        ]);

        const orgData = orgRes?.data || {};
        const userData = userRes?.data || {};

        setAllBankDetails({
          individualBankDetails: userData.individual_bankDetails || {},
          organisationBankDetails: userData.organisation_bankDetails || {},
        });

        const bankDetails =
          userData.type === "Individual"
            ? userData.individual_bankDetails || {}
            : userData.organisation_bankDetails || {};

        setState((prev) => ({
          ...prev,
          userDetails: {
            personal: {
              firstName: userData.individual_firstName || currentUser?.firstName || "",
              lastName: userData.individual_lastName || currentUser?.lastName || "",
              email: userData.individual_email || currentUser?.email || "",
              phone: userData.individual_phone || currentUser?.phone || "",
              profileImage: userData.individual_profileImage || currentUser?.profileImage || "",
            },
            organization: {
              name: orgData.name || "",
              domain: orgData.domain || "",
            },
            address: {
              line1: userData.individual_homeAddress || orgData.address?.line1 || "",
              city: userData.individual_personal_city || orgData.address?.city || "",
              postalCode: userData.individual_personal_postalCode || orgData.address?.postalCode || "",
              country: userData.individual_personal_country || orgData.address?.country || "",
            },
          },
          bankDetails: bankDetails,
        }));
      } catch (error) {
        console.error("Error fetching user or organization details:", error);
      }
    };

    fetchUserDetails();
  }, []);

  const fetchCounterPartyDetails = async () => {
    try {
      if (!counterPartyId) return;
      const res = await getSingleAddressDetails(counterPartyId);
      const cpData = res?.data || {};

      setState((prev) => ({
        ...prev,
        counterParty: {
          details: {
            name:
              cpData.organisation_name ||
              `${cpData.individual_firstName || ""} ${cpData.individual_lastName || ""}`.trim() ||
              "Unknown Counterparty",
            contactPerson: cpData.organisation_contactPerson || cpData.individual_contactPerson || "",
            phone: cpData.organisation_phone || cpData.individual_phone || "",
            email: cpData.organisation_email || cpData.individual_email || "",
          },
          address: {
            line1: cpData.organisation_address || cpData.individual_homeAddress || "",
            city: cpData.organisation_city || cpData.individual_personal_city || "",
            state: cpData.organisation_state || cpData.individual_personal_state || "",
            postalCode: cpData.organisation_postalCode || cpData.individual_personal_postalCode || "",
            country: cpData.organisation_country || cpData.individual_personal_country || "",
          },
        },
      }));
    } catch (error) {
      console.error("Error fetching counter party details:", error);
    }
  };

  useEffect(() => {
    fetchCounterPartyDetails();
  }, [counterPartyId]);

  const handleAddDeduction = () => {
    if (state.newDeduction.label && state.newDeduction.value) {
      setState((prev) => ({
        ...prev,
        deductions: [...prev.deductions, prev.newDeduction],
        newDeduction: { label: "", value: "" },
      }));
    }
  };

  const handleRemoveDeduction = (index) => {
    setState((prev) => {
      const updatedDeductions = [...prev.deductions];
      updatedDeductions.splice(index, 1);
      return { ...prev, deductions: updatedDeductions };
    });
  };

  const handleSaveChanges = async () => {
    const invoiceDetailList = state.invoiceDetail;
    const separated = {
      cargo: invoiceDetailList
        .filter((item) => item.invoiceDetail?.isCargo)
        .map((item) => item.invoiceDetail),
      vessel: invoiceDetailList
        .filter((item) => !item.invoiceDetail?.isCargo)
        .map((item) => ({ ...item.invoiceDetail, vesselType: item?.vesselType })),
    };
    await editInvoice({ finalPdfData: state, separated, id: invoiceId, contractType });
    refetch?.();
    handleModalClose?.();
  };

  const renderUserAddress = () => {
    const { personal, organization, address } = state.userDetails;
    return (
      <div className="border border-slate-200 bg-slate-50/50 p-2 text-[11px] font-medium text-slate-700 h-full flex flex-col justify-between">
        <div>
          <div className="flex items-center justify-between border-b border-slate-200 pb-1 mb-1.5">
            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Bill From</span>
            {organization.name && (
              <span className="text-[10px] bg-slate-200 text-slate-700 px-1.5 py-[1px] font-medium">
                {organization.name}
              </span>
            )}
          </div>
          <p className="font-semibold text-slate-900 text-[11px]">
            {personal.firstName} {personal.lastName}
          </p>
          {personal.email && <p className="text-[11px] text-slate-600 truncate">Email: {personal.email}</p>}
          {personal.phone && <p className="text-[11px] text-slate-600">Phone: {personal.phone}</p>}
        </div>
        <p className="text-[10.5px] text-slate-500 mt-1 border-t border-slate-200/60 pt-1 leading-tight">
          {[address.line1, address.city, address.postalCode, address.country].filter(Boolean).join(", ") || "Address not available"}
        </p>
      </div>
    );
  };

  const renderCounterPartyAddress = () => {
    const { details, address } = state.counterParty;
    if (!details.name) {
      return (
        <div className="border border-slate-200 bg-slate-50/50 p-2 text-[11px] text-slate-400 italic h-full flex items-center justify-center">
          Loading counterparty details...
        </div>
      );
    }

    return (
      <div className="border border-slate-200 bg-slate-50/50 p-2 text-[11px] font-medium text-slate-700 h-full flex flex-col justify-between">
        <div>
          <div className="flex items-center justify-between border-b border-slate-200 pb-1 mb-1.5">
            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Bill To</span>
            {details.contactPerson && (
              <span className="text-[10px] bg-sky-50 text-sky-800 border border-sky-200 px-1.5 py-[1px] font-medium truncate max-w-[140px]">
                Attn: {details.contactPerson}
              </span>
            )}
          </div>
          <p className="font-semibold text-slate-900 text-[11px]">{details.name}</p>
          {details.email && <p className="text-[11px] text-slate-600 truncate">Email: {details.email}</p>}
          {details.phone && <p className="text-[11px] text-slate-600">Phone: {details.phone}</p>}
        </div>
        <p className="text-[10.5px] text-slate-500 mt-1 border-t border-slate-200/60 pt-1 leading-tight">
          {[address.line1, address.city, address.state, address.postalCode, address.country].filter(Boolean).join(", ") || "Address not available"}
        </p>
      </div>
    );
  };

  const renderBankDetails = () => {
    const bank = state.bankDetails || {};
    const bankFields = [
      { label: "Account Name", value: bank.accountName },
      { label: "Bank Name", value: bank.bankName },
      { label: "Account Number", value: bank.accountNumber },
      { label: "IFSC Code", value: bank.ifscCode },
      { label: "Branch", value: bank.branch },
      { label: "Currency", value: bank.currency },
      ...(bank.extra?.map((item) => ({ label: item.label, value: item.value })) || []),
    ].filter((field) => field.value);

    return (
      <div className="border border-slate-200 bg-white">
        <div className="px-2 py-1 bg-slate-100 border-b border-slate-200 flex items-center justify-between">
          <span className="text-[10.5px] font-semibold text-slate-800 uppercase tracking-wider">Bank Details</span>
          <button
            type="button"
            onClick={handleEditBankDetails}
            className="text-sky-600 hover:text-sky-800 flex items-center gap-1 text-[10px] font-medium cursor-pointer"
          >
            <MdOutlineEdit className="w-3.5 h-3.5" />
            <span>Edit</span>
          </button>
        </div>
        <div className="p-2">
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
            {bankFields.length > 0 ? (
              bankFields.map((field, index) => (
                <div key={index} className="flex justify-between border-b border-slate-100 py-0.5">
                  <span className="text-slate-500">{field.label}:</span>
                  <span className="text-slate-900 font-medium ml-1 truncate">{field.value}</span>
                </div>
              ))
            ) : (
              <p className="text-slate-400 italic col-span-2 text-center py-1">No bank details configured</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="text-[11px] font-medium text-slate-800 space-y-2">
      {/* Top Banner with Actions */}
      <div className="flex items-center justify-between bg-slate-50 border border-slate-200 px-3 py-1.5">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-slate-900 text-xs tracking-wide">
            {isEdit ? "EDIT INVOICE" : "CREATE INVOICE"}
          </span>
          <span className="text-slate-300">|</span>
          <span className="text-slate-600 text-[11px]">
            Date: {moment().format("DD/MM/YYYY")}
          </span>
          {isEdit && (
            <span className="bg-amber-100 text-amber-800 text-[10px] px-1.5 py-[1px] font-medium">
              Editing Mode
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!isEdit ? (
            <DownloadInvoice
              setIsLoading={setIsLoading}
              refetch={refetch}
              bankDetails={state.bankDetails}
              handleModalClose={handleModalClose || onClose}
              opsId={opsId}
              invoiceData={state}
              contractType={contractType}
              invoiceDetail={state.invoiceDetail}
            />
          ) : (
            <button
              onClick={handleSaveChanges}
              className="bg-[#313C4C] hover:bg-[#0d508f] text-white px-3 py-1 text-[11px] font-medium transition shadow-xs cursor-pointer flex items-center gap-1"
            >
              <span>Save Changes</span>
            </button>
          )}
        </div>
      </div>

      {/* Bill From & Bill To Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div>{renderUserAddress()}</div>
        <div>{renderCounterPartyAddress()}</div>
      </div>

      {/* Invoice Line Items Table */}
      <div className="border border-slate-200 overflow-hidden bg-white">
        <div className="bg-slate-100 px-2 py-1 border-b border-slate-200 flex items-center justify-between">
          <span className="text-[10.5px] font-semibold text-slate-800 uppercase tracking-wider">
            Line Items ({state.invoiceDetail?.length || 0})
          </span>
        </div>
        <div className="overflow-x-auto scroll-thin">
          <table className="min-w-full border-collapse text-[11px]">
            <thead className="bg-[#e2e8f0] text-[#1e293b] h-[22px]">
              <tr>
                <th className="px-1.5 py-0.5 border-r border-b border-slate-300 text-left font-medium w-28">Label</th>
                <th className="px-2 py-0.5 border-r border-b border-slate-300 text-left font-medium">Description</th>
                <th className="px-1.5 py-0.5 border-r border-b border-slate-300 text-center font-medium w-20">B/L Date</th>
                <th className="px-1.5 py-0.5 border-r border-b border-slate-300 text-center font-medium w-16">B/L Qty</th>
                <th className="px-1.5 py-0.5 border-r border-b border-slate-300 text-center font-medium w-20">Remarks</th>
                <th className="px-1.5 py-0.5 border-r border-b border-slate-300 text-center font-medium w-24">Port</th>
                <th className="px-1.5 py-0.5 border-r border-b border-slate-300 text-center font-medium w-20">Operation</th>
                <th className="px-1.5 py-0.5 border-r border-b border-slate-300 text-center font-medium w-24">Cargo/Vessel</th>
                <th className="px-2 py-0.5 border-b border-slate-300 text-right font-medium w-24">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(state.invoiceDetail || []).map((item, index) => (
                <tr key={index} className="hover:bg-amber-50/40 transition-colors h-[22px]">
                  <td className="px-1.5 py-0.5 border-r border-b border-slate-200 align-middle">
                    <div className="flex items-center justify-between gap-1">
                      {state.editingLabel === index ? (
                        <input
                          value={item.invoiceDetail?.label || item.label || ""}
                          onChange={(e) => handleDescriptionUpdate(index, e.target.value, 'label')}
                          onBlur={() => toggleEditDescription(index, 'editingLabel')}
                          className="w-full px-1 py-0 text-[11px] border border-amber-400 bg-amber-50 rounded-none outline-none"
                          autoFocus
                        />
                      ) : (
                        <>
                          <span className="truncate font-medium text-slate-800">{item.invoiceDetail?.label || item.label}</span>
                          <button
                            type="button"
                            className="text-sky-500 hover:text-sky-700 flex-shrink-0"
                            onClick={() => toggleEditDescription(index, 'editingLabel')}
                            title="Edit label"
                          >
                            <MdOutlineEdit className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-0.5 border-r border-b border-slate-200 align-middle max-w-[220px]">
                    <div className="flex items-center justify-between gap-1">
                      {state.editingDescription === index ? (
                        <input
                          value={item.invoiceDetail?.description || item.description || ""}
                          onChange={(e) => handleDescriptionUpdate(index, e.target.value, 'description')}
                          onBlur={() => toggleEditDescription(index, 'editingDescription')}
                          className="w-full px-1 py-0 text-[11px] border border-amber-400 bg-amber-50 rounded-none outline-none"
                          autoFocus
                        />
                      ) : (
                        <>
                          {item.invoiceDetail?.description || item.description ? (
                            <CustomTooltip text={item.invoiceDetail?.description || item.description} position="top">
                              <span className="truncate block max-w-[190px] cursor-help text-slate-700">
                                {item.invoiceDetail?.description || item.description}
                              </span>
                            </CustomTooltip>
                          ) : (
                            <span className="text-slate-400 italic text-[10.5px]">—</span>
                          )}
                          <button
                            type="button"
                            className="text-sky-500 hover:text-sky-700 flex-shrink-0"
                            onClick={() => toggleEditDescription(index, 'editingDescription')}
                            title="Edit description"
                          >
                            <MdOutlineEdit className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-1.5 py-0.5 border-r border-b border-slate-200 text-center align-middle text-slate-600">
                    {item.invoiceDetail?.blDate ? moment(item.invoiceDetail.blDate).format("DD/MM/YY") : "-"}
                  </td>
                  <td className="px-1.5 py-0.5 border-r border-b border-slate-200 text-center align-middle text-slate-600">
                    {item.invoiceDetail?.blQty || "-"}
                  </td>
                  <td className="px-1.5 py-0.5 border-r border-b border-slate-200 text-center align-middle text-slate-600">
                    {item.invoiceDetail?.remarks || "-"}
                  </td>
                  <td className="px-1.5 py-0.5 border-r border-b border-slate-200 align-middle">
                    <div className="flex items-center justify-between gap-1">
                      <span className="truncate text-slate-700">{item.port || "-"}</span>
                      {item.port && !item.showUndoPort ? (
                        <button
                          type="button"
                          className="text-rose-500 hover:text-rose-700 flex-shrink-0"
                          onClick={() => handlePortRemove(index)}
                          title="Remove port"
                        >
                          <span className="text-xs font-bold leading-none">×</span>
                        </button>
                      ) : item.showUndoPort ? (
                        <button
                          type="button"
                          className="text-sky-600 hover:text-sky-800 flex-shrink-0"
                          onClick={() => handlePortUndo(index)}
                          title="Undo port remove"
                        >
                          <LuUndo className="w-3 h-3" />
                        </button>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-1.5 py-0.5 border-r border-b border-slate-200 text-center align-middle text-slate-700">
                    {item.operation || "-"}
                  </td>
                  <td className="px-1.5 py-0.5 border-r border-b border-slate-200 align-middle">
                    <div className="flex items-center justify-between gap-1">
                      <span className="truncate text-slate-700">{item.cargoName || item.vesselName || "-"}</span>
                      {(item.cargoName || item.vesselName) && !item.showUndo ? (
                        <button
                          type="button"
                          className="text-rose-500 hover:text-rose-700 flex-shrink-0"
                          onClick={() => handleCargoVesselRemove(index)}
                          title="Remove cargo/vessel"
                        >
                          <span className="text-xs font-bold leading-none">×</span>
                        </button>
                      ) : item.showUndo ? (
                        <button
                          type="button"
                          className="text-sky-600 hover:text-sky-800 flex-shrink-0"
                          onClick={() => handleUndo(index)}
                          title="Undo remove"
                        >
                          <LuUndo className="w-3 h-3" />
                        </button>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-2 py-0.5 border-b border-slate-200 text-right font-semibold text-slate-900 align-middle bg-[#f0f4f9]">
                    {parseFloat(item.invoiceDetail?.amt || item.amount || 0).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bottom Section: Deductions + Financial Summary & Bank Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-1">
        {/* Left Side: Deductions & Bank Details */}
        <div className="space-y-2">
          {/* Add Deductions Card */}
          <div className="border border-slate-200 bg-white p-2">
            <span className="text-[10.5px] font-semibold text-slate-800 uppercase tracking-wider block mb-1.5">
              Add Deduction
            </span>
            <div className="flex items-center gap-1.5">
              <input
                type="text"
                placeholder="Deduction Label (e.g. Advance, Discount)"
                value={state.newDeduction.label}
                onChange={(e) =>
                  setState((prev) => ({
                    ...prev,
                    newDeduction: { ...prev.newDeduction, label: e.target.value },
                  }))
                }
                className="flex-1 px-2 py-0.5 border border-slate-300 text-[11px] rounded-none outline-none focus:border-amber-500"
              />
              <input
                type="number"
                placeholder="Amount"
                value={state.newDeduction.value}
                onChange={(e) =>
                  setState((prev) => ({
                    ...prev,
                    newDeduction: { ...prev.newDeduction, value: e.target.value },
                  }))
                }
                className="w-24 px-2 py-0.5 border border-slate-300 text-[11px] rounded-none outline-none focus:border-amber-500"
              />
              <button
                type="button"
                onClick={handleAddDeduction}
                className="bg-[#313C4C] hover:bg-[#0d508f] text-white px-2.5 py-0.5 text-[11px] rounded-none transition cursor-pointer flex items-center gap-1"
              >
                <LuPlus size={12} />
                <span>Add</span>
              </button>
            </div>

            {/* List of Applied Deductions */}
            {state.deductions?.length > 0 && (
              <div className="mt-2 space-y-1 border-t border-slate-100 pt-1.5">
                {state.deductions.map((ded, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between text-[11px] bg-rose-50/60 border border-rose-100 px-2 py-0.5"
                  >
                    <span className="text-slate-700">{ded.label}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-rose-600">
                        -{parseFloat(ded.value || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveDeduction(idx)}
                        className="text-rose-500 hover:text-rose-700 font-bold text-xs cursor-pointer"
                        title="Delete deduction"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Bank Details */}
          {renderBankDetails()}
        </div>

        {/* Right Side: Financial Summary */}
        <div className="border border-slate-200 bg-slate-50/50 p-3 h-full flex flex-col justify-between">
          <div>
            <span className="text-[10.5px] font-semibold text-slate-800 uppercase tracking-wider block border-b border-slate-200 pb-1 mb-2">
              Invoice Summary
            </span>
            <div className="space-y-1.5 text-[11px]">
              <div className="flex justify-between text-slate-600">
                <span>Subtotal:</span>
                <span className="font-medium text-slate-900">
                  {state.totals.subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </span>
              </div>
              {state.deductions?.map((ded, idx) => (
                <div key={idx} className="flex justify-between text-rose-600">
                  <span>Deduction ({ded.label}):</span>
                  <span className="font-medium">
                    -{parseFloat(ded.value || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                </div>
              ))}
              <div className="flex justify-between border-t border-slate-300 pt-2 text-xs font-bold text-slate-900">
                <span>Total Due:</span>
                <span className="text-sm font-bold text-[#0f4c81]">
                  {state.totals.finalTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-4 pt-2 border-t border-slate-200 flex items-center justify-between text-[10.5px] text-slate-500">
            <span>Currency: USD</span>
            <span>Tax Status: Standard</span>
          </div>
        </div>
      </div>

      {/* Edit Bank Details Modal */}
      <CustomAddModal
        open={isBankModalOpen}
        saveLabel={"Save"}
        title="Edit Bank Details"
        maxWidth="sm"
        handleClose={() => setIsBankModalOpen(false)}
        onSubmitConfirm={handleSaveBankDetails}
      >
        <div className="text-[11px] space-y-2 p-1">
          {/* Bank Type Switcher */}
          <div className="flex bg-slate-100 p-0.5 border border-slate-200">
            <button
              type="button"
              className={`flex-1 py-1 text-center font-medium transition ${
                selectedBankType === "individual"
                  ? "bg-white text-[#0f4c81] font-semibold shadow-xs"
                  : "text-slate-600 hover:text-slate-900"
              }`}
              onClick={() => handleBankTypeChange("individual")}
            >
              Individual
            </button>
            <button
              type="button"
              className={`flex-1 py-1 text-center font-medium transition ${
                selectedBankType === "organisation"
                  ? "bg-white text-[#0f4c81] font-semibold shadow-xs"
                  : "text-slate-600 hover:text-slate-900"
              }`}
              onClick={() => handleBankTypeChange("organisation")}
            >
              Organisation
            </button>
          </div>

          <div className="space-y-1.5">
            <div>
              <label className="block text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">Account Name</label>
              <input
                type="text"
                value={localBankDetails.accountName || ""}
                onChange={(e) => handleFieldChange("accountName", e.target.value)}
                className="w-full px-2 py-1 text-[11px] border border-slate-300 rounded-none outline-none focus:border-amber-500"
                placeholder="Enter account name"
              />
            </div>
            <div>
              <label className="block text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">Bank Name</label>
              <input
                type="text"
                value={localBankDetails.bankName || ""}
                onChange={(e) => handleFieldChange("bankName", e.target.value)}
                className="w-full px-2 py-1 text-[11px] border border-slate-300 rounded-none outline-none focus:border-amber-500"
                placeholder="Enter bank name"
              />
            </div>
            <div>
              <label className="block text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">Account Number</label>
              <input
                type="text"
                value={localBankDetails.accountNumber || ""}
                onChange={(e) => handleFieldChange("accountNumber", e.target.value)}
                className="w-full px-2 py-1 text-[11px] border border-slate-300 rounded-none outline-none focus:border-amber-500"
                placeholder="Enter account number"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">IFSC / SWIFT</label>
                <input
                  type="text"
                  value={localBankDetails.ifscCode || ""}
                  onChange={(e) => handleFieldChange("ifscCode", e.target.value)}
                  className="w-full px-2 py-1 text-[11px] border border-slate-300 rounded-none outline-none focus:border-amber-500"
                  placeholder="IFSC / SWIFT"
                />
              </div>
              <div>
                <label className="block text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">Branch</label>
                <input
                  type="text"
                  value={localBankDetails.branch || ""}
                  onChange={(e) => handleFieldChange("branch", e.target.value)}
                  className="w-full px-2 py-1 text-[11px] border border-slate-300 rounded-none outline-none focus:border-amber-500"
                  placeholder="Branch"
                />
              </div>
            </div>

            {/* Extra Bank Fields */}
            {localBankDetails.extra?.length > 0 && (
              <div className="pt-1.5 border-t border-slate-200 space-y-1">
                <span className="text-[10px] text-slate-500 uppercase tracking-wider block">Additional Fields</span>
                {localBankDetails.extra.map((item, index) => (
                  <div key={index} className="flex gap-1 items-center">
                    <input
                      type="text"
                      placeholder="Label"
                      value={item.label}
                      onChange={(e) => handleExtraChange(index, "label", e.target.value)}
                      className="flex-1 px-1.5 py-0.5 text-[11px] border border-slate-300 rounded-none outline-none"
                    />
                    <input
                      type="text"
                      placeholder="Value"
                      value={item.value}
                      onChange={(e) => handleExtraChange(index, "value", e.target.value)}
                      className="flex-1 px-1.5 py-0.5 text-[11px] border border-slate-300 rounded-none outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveExtra(index)}
                      className="text-rose-500 hover:text-rose-700 font-bold px-1"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={handleAddExtra}
              className="text-[#0f4c81] hover:underline text-[10.5px] font-medium flex items-center gap-1 mt-1 cursor-pointer"
            >
              <LuPlus size={12} />
              <span>Add Custom Field</span>
            </button>
          </div>
        </div>
      </CustomAddModal>
    </div>
  );
};

export default CreateInvoiceModal;